import React, { useState } from "react";
import styles from "../../assets/scss/Ledger/Ledgertable.module.scss";
import { useGetNetProfitQuery } from "../../slices/admin/admin";

const NetProfit = () => {
    // Default to current month and year
    const [selectedMonth, setSelectedMonth] = useState(String(new Date().getMonth() + 1));
    const [selectedYear, setSelectedYear] = useState(String(new Date().getFullYear()));

    const { data: netProfitResponse, isLoading, isError } = useGetNetProfitQuery({
        month: selectedMonth,
        year: selectedYear
    }, {
        skip: !selectedMonth || !selectedYear // Prevent fetching if both are not selected
    });

    const clearFilters = () => {
        setSelectedMonth("");
        setSelectedYear("");
    };

    const netProfitData = netProfitResponse?.data || {
        totalRevenue: 0,
        totalExpense: 0,
        totalNetProfit: 0,
    };

    const months = [
        { value: "1", label: "January" },
        { value: "2", label: "February" },
        { value: "3", label: "March" },
        { value: "4", label: "April" },
        { value: "5", label: "May" },
        { value: "6", label: "June" },
        { value: "7", label: "July" },
        { value: "8", label: "August" },
        { value: "9", label: "September" },
        { value: "10", label: "October" },
        { value: "11", label: "November" },
        { value: "12", label: "December" },
    ];

    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

    return (
        <div >
            {/* Filters */}
            <div className={styles.filterBar}>
                <select
                    className={styles.inputs}
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                >
                    <option value="" disabled hidden>Select Month</option>
                    {months.map((month) => (
                        <option key={month.value} value={month.value}>
                            {month.label}
                        </option>
                    ))}
                </select>

                <select
                    className={styles.inputs}
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(e.target.value)}
                >
                    <option value="" disabled hidden>Select Year</option>
                    {years.map((year) => (
                        <option key={year} value={year}>
                            {year}
                        </option>
                    ))}
                </select>

                <button className={styles.clearBtn} onClick={clearFilters}>
                    Clear All
                </button>
            </div>

            {/* Net Profit Display */}
            <div className={styles.tableWrapper} style={{ padding: "20px" }}>
                {isLoading ? (
                    <p style={{ textAlign: "center", color: "#6c757d" }}>Loading net profit data...</p>
                ) : isError ? (
                    <p style={{ textAlign: "center", color: "#dc3545" }}>Error fetching net profit data.</p>
                ) : !selectedMonth || !selectedYear ? (
                    <p style={{ textAlign: "center", color: "#6c757d" }}>Please select a month and year to view the net profit.</p>
                ) : (
                    <div className={styles.cardContainer}>
                        
                        {/* Sales Section */}
                        <div className={styles.profitCard}>
                            <h3 className={styles.cardTitle}>Sales Breakdown</h3>
                            <div className={styles.cardRow}>
                                <span>Received Amount:</span>
                                <span className={styles.value}>₹ {netProfitData.totalSalesReceivedAmount?.toLocaleString() ?? 0}</span>
                            </div>
                            <div className={styles.cardRow}>
                                <span>Pending Amount:</span>
                                <span className={styles.value}>₹ {netProfitData.totalSalesPendingAmount?.toLocaleString() ?? 0}</span>
                            </div>
                            <div className={styles.totalRowBordered}>
                                <strong className={styles.green}>Total Sales:</strong>
                                <strong className={styles.green}>₹ {netProfitData.totalSalesAmount?.toLocaleString() ?? 0}</strong>
                            </div>
                        </div>

                        {/* Purchase Section */}
                        <div className={styles.profitCard}>
                            <h3 className={styles.cardTitle}>Purchase Breakdown</h3>
                            <div className={styles.cardRow}>
                                <span>Received Amount:</span>
                                <span className={styles.value}>₹ {netProfitData.totalPurchaseReceivedAmount?.toLocaleString() ?? 0}</span>
                            </div>
                            <div className={styles.cardRow}>
                                <span>Pending Amount:</span>
                                <span className={styles.value}>₹ {netProfitData.totalPurchasePendingAmount?.toLocaleString() ?? 0}</span>
                            </div>
                            <div className={styles.totalRowBordered}>
                                <strong className={styles.red}>Total Purchase:</strong>
                                <strong className={styles.red}>₹ {netProfitData.totalPurchaseAmount?.toLocaleString() ?? 0}</strong>
                            </div>
                        </div>

                        {/* Final Calculation Section */}
                        <div className={`${styles.profitCard} ${styles.calculationSection}`}>
                            <h3 className={`${styles.cardTitle} ${styles.calcTitle}`}>Net Profit Calculation</h3>
                            
                            <div className={styles.calcRow}>
                                <span>Total Sales:</span>
                                <span className={styles.greenValue}>₹ {netProfitData.totalSalesAmount?.toLocaleString() ?? 0}</span>
                            </div>
                            
                            <div className={styles.calcRow}>
                                <span>Total Purchase:</span>
                                <span className={styles.redValue}>- ₹ {netProfitData.totalPurchaseAmount?.toLocaleString() ?? 0}</span>
                            </div>
                            
                            <div className={styles.revenueRow}>
                                <strong>Total Revenue:</strong>
                                <strong className={styles.value}>₹ {netProfitData.totalRevenue?.toLocaleString() ?? 0}</strong>
                            </div>

                            <div className={styles.calcRow}>
                                <span>Total Expenses:</span>
                                <span className={styles.redValue}>- ₹ {netProfitData.totalExpense?.toLocaleString() ?? 0}</span>
                            </div>
                            
                            <div className={styles.finalProfitRow}>
                                <strong>Net Profit:</strong>
                                <strong className={styles.profitBadge}>
                                    ₹ {netProfitData.totalNetProfit?.toLocaleString() ?? 0}
                                </strong>
                            </div>
                        </div>

                    </div>
                )}
            </div>
        </div>
    );
};

export default NetProfit;
